import { call, takeEvery, put, select } from 'redux-saga/effects'
import { buyUserActions, BuyUserActionTypes } from '@buy/state/user/actions'
import commonServices from '@common/services'
import {
    getContentData,
    getLabelContent,
    getPlanData,
    getProductSettingsData,
    getUserState,
} from '@buy/state/selectors'
import { authClass, processExistingBillingInfoUCS } from '@common/utils'
import { trackAbandonForm } from '@buy/utils'
import { BuyUserState } from '@buy/state/user/state'
import { ExistingBillingInfoType, ExistingUserInfoType } from '@common/state/user/state'
import { buyFormActions } from '@buy/state/buy-form/actions'
import { BuyFormData } from '@buy/state/buy-form/state'
import { COUNTRIES, STORAGE_CONST } from '@common/constants'
import { LabelContent } from '@buy/state/label-content'
import { BUY_USER_TYPES, CHECKOUTFORM_FIELDS } from '@buy/constants'
import { fatalErrorActions } from '@common/state/fatal-error/actions'
import { isEmpty } from 'lodash-es'
import { getCountryFromCookie, setCountryInCookie } from '@common/utils/cookie-utils'
import { BuyContentData } from '@buy/state/content-data'
import { ProductSettings } from '@buy/state/product-settings'
import { AxiosResponse } from 'axios'
import { getAddressUCS, getTaxDetailsUCS, validateUserUCS } from '@buy/services'
import { ValidateUserRequestBody, ValidateUserResponseBody } from '@buy/state/validate-user/state'
import { BuyPlan } from '@buy/state/plan'
import { GetAddressResponseBody } from '@buy/state/get-address/state'
import { GetTaxDetailsResponseBody } from '@buy/state/get-tax-details/state'

function* userLoggedInUcsSaga() {
    try {
        const productSettings: ProductSettings = yield select(getProductSettingsData)
        yield put(buyUserActions.setLoadingExistingInformation(true))
        const {
            loggedIn: { accessToken },
        }: BuyUserState = yield select(getUserState)
        const { disabledUserError, internalErrorMessage }: LabelContent = yield select(getLabelContent)
        const { marketoProductName }: BuyContentData = yield select(getContentData)

        const planData: BuyPlan = yield select(getPlanData)

        // clear previous session on the server
        yield call(commonServices.clearUserSession)
        // get user info
        // eslint-disable-next-line camelcase
        const validateUserData: ValidateUserRequestBody = {
            productFamily: marketoProductName,
            productSku: planData.sKU,
        }

        let validateUserResponse: AxiosResponse<ValidateUserResponseBody> | undefined
        try {
            validateUserResponse = yield call(validateUserUCS, accessToken?.access_token || '', validateUserData)
        } catch (e: any) {
            const status = e.response?.status ?? undefined
            const errorCode = e.response?.data?.errors[0]?.code ?? undefined
            if (status === 403 && errorCode === 'error.core.collab.user') {
                // return error message if user is a non-billingAdmin AND (already part of account, which
                // has core-collab product) AND trying to buy any core-collab product
                yield put(buyUserActions.setLoadingExistingInformation(false))
                yield put(
                    fatalErrorActions.setFatalError(
                        true,
                        { fatalErrorMessage: internalErrorMessage, trackErrorMessage: internalErrorMessage },
                        true
                    )
                )
            } else {
                // Other errors
                yield put(buyUserActions.setLoadingExistingInformation(false))
                yield put(
                    fatalErrorActions.setFatalError(
                        true,
                        { fatalErrorMessage: internalErrorMessage, trackErrorMessage: internalErrorMessage },
                        true
                    )
                )
            }
            return
        }

        if (validateUserResponse && !isEmpty(validateUserResponse.data)) {
            const { user, account, redirectToGotoAdmin } = validateUserResponse.data

            // If the user doesn't have an account, then treat them as a new user.
            // TODO: There is a rare scenario where user might pass canUserAuthenticate because they are a CIP User
            // but they aren't a billing admin. So they could get into a state where they are prompted over and over
            // to sign in. We should handle this case separately.
            if (!account) {
                yield put(buyUserActions.setUserType(BUY_USER_TYPES.NEW))
                yield put(buyUserActions.setLoadingExistingInformation(false))
                yield call(authClass().logout)
                return
            }

            // return error message if user is banned
            if (account.banned === true) {
                yield put(buyUserActions.setUserType(BUY_USER_TYPES.BANNED))
                yield put(buyUserActions.setLoadingExistingInformation(false))
                yield put(
                    fatalErrorActions.setFatalError(
                        true,
                        { fatalErrorMessage: disabledUserError, trackErrorMessage: disabledUserError },
                        true
                    )
                )
                return
            }

            // redirect if user is attempting same-product purchase flow
            if (redirectToGotoAdmin && account.hasSubscription) {
                trackAbandonForm()
                window.location.href = productSettings.adminUrl
                return
            }

            const userInfo: ExistingUserInfoType = {
                UserName: user.email,
                Name: { GivenName: user.firstname, FamilyName: user.lastname },
                UserKey: user.key,
                AccountKey: account.accountKey,
            }

            const isSameAsAccount = sessionStorage.getItem('useSameForBilling') !== 'false'
            const buyFormData: BuyFormData = {
                FirstName: user.firstname,
                LastName: user.lastname,
                Email: user.email,
                Street1: '',
                City: '',
                ZipCode: '',
                StateCode: '',
                CountryCode:
                    sessionStorage.getItem(CHECKOUTFORM_FIELDS.COUNTRY_CODE) || getCountryFromCookie() || COUNTRIES.US,
                ContactPhone: '',
                Password: '',
                BillingFirstName: !isSameAsAccount
                    ? sessionStorage.getItem(CHECKOUTFORM_FIELDS.BILLING_FIRST_NAME) || ''
                    : user.firstname,
                BillingLastName: !isSameAsAccount
                    ? sessionStorage.getItem(CHECKOUTFORM_FIELDS.BILLING_LAST_NAME) || ''
                    : user.lastname,
                ExpirationDate: '',
                TaxNumber: '',
                TaxType: '',
                CompanyName: '',
                Disclaimer: false,
            }

            // set up and display existing user account information
            yield put(buyUserActions.setExistingUserInformation(userInfo))

            // #region attempt to set up existing billing info
            // Get billing address
            const addressResponse: AxiosResponse<GetAddressResponseBody> = yield call(
                getAddressUCS,
                accessToken?.access_token || '',
                account.accountKey
            )

            // Get tax details
            let taxDetails: GetTaxDetailsResponseBody = {}
            try {
                // If tax details are available, set them in the buy form
                const taxDetailsResponse: AxiosResponse<GetTaxDetailsResponseBody> = yield call(
                    getTaxDetailsUCS,
                    accessToken?.access_token || '',
                    account.accountKey
                )
                if (taxDetailsResponse && !isEmpty(taxDetailsResponse.data)) {
                    taxDetails = taxDetailsResponse.data
                }
            } catch (e: any) {
                const status = e.response?.status ?? undefined
                const errorCode = e.response?.data?.errors[0]?.code ?? undefined
                if (status === 404 && errorCode === 'taxDetails.not.found') {
                    // If tax details don't exist, then the API sends back a 404 status with
                    // error code 'taxDetails.not.found'. But that's okay, because it just
                    // means the account doesn't have tax details
                    taxDetails = {}
                } else {
                    throw Error(e)
                }
            }

            if (addressResponse && !isEmpty(addressResponse.data)) {
                // We get company name same as the email without the `@` in case we don't provide in the first place.
                // This is a known behavior for GTC. So, we check for GTR, we check if that's what we got and don't show the value
                const { billingAddress } = addressResponse.data
                const { email: billingEmail, companyName } = billingAddress
                if (billingEmail.replace('@', '').replace('_', '') === companyName) {
                    addressResponse.data.billingAddress.companyName = ''
                }

                const processedBillingData = processExistingBillingInfoUCS(billingAddress, taxDetails)
                const billingInfo: ExistingBillingInfoType = processedBillingData
                const currentCountry =
                    sessionStorage.getItem(CHECKOUTFORM_FIELDS.COUNTRY_CODE) || getCountryFromCookie() || COUNTRIES.US

                if (billingInfo.state === 'XX') {
                    billingInfo.state = ''
                    yield put(buyFormActions.setStatesOptional(true))
                }

                // If address IS set, but GeoIP is different from existing user country,
                // reload such that buy form country matches that of GeoIP cookie
                if (billingInfo.addressLine1) {
                    if (billingInfo.country !== currentCountry) {
                        setCountryInCookie(billingInfo.country)
                        sessionStorage.setItem(CHECKOUTFORM_FIELDS.COUNTRY_CODE, billingInfo.country)
                        window.location.reload()
                    }
                } else {
                    billingInfo.country = currentCountry
                }

                billingInfo.firstName = user.firstname
                billingInfo.lastName = user.lastname
                buyFormData.Street1 = billingInfo.addressLine1
                buyFormData.City = billingInfo.city
                buyFormData.CountryCode = billingInfo.country
                buyFormData.ZipCode = billingInfo.postalCode
                buyFormData.StateCode = billingInfo.state
                buyFormData.ContactPhone = billingInfo.phoneNumber
                buyFormData.TaxNumber = billingInfo.taxNumber || ''
                buyFormData.TaxType = billingInfo.federalTaxType || ''

                // all five fields must contain values in order to set existing billing information
                if (buyFormData.Street1 && buyFormData.City && buyFormData.CountryCode && buyFormData.ZipCode) {
                    yield put(buyUserActions.setExistingBillingInformation(billingInfo))
                }
            }
            yield put(buyFormActions.updateInitialFormState(buyFormData))
            // #endregion

            // if user is trying cross-product purchase flow, auto-submit the form
            // see src/products/buy/saga/checkout-form/existing-user-form-submission-ucs.ts
            if (redirectToGotoAdmin && !account.hasSubscription) {
                yield put(buyUserActions.setUserType(BUY_USER_TYPES.CROSSPRODUCT))
                yield put(buyUserActions.setLoadingExistingInformation(false))
                return
            }

            // if userType was saved to sessionStorage for a new user before the redirect to OnePay but the user clicked Back on onepay, they'll end up here
            // see src/products/buy/saga/checkout-form/form-submission-ucs.ts
            const userTypeFromSessionStorage = window.sessionStorage.getItem(STORAGE_CONST.USER_TYPE)
            if (userTypeFromSessionStorage) {
                yield put(buyUserActions.setUserType(userTypeFromSessionStorage))
                yield put(buyUserActions.setLoadingExistingInformation(false))
                return
            }

            // If the user type wasn't set by something else above, then the only other option remaining is Trial Conversion
            yield put(buyUserActions.setUserType(BUY_USER_TYPES.TRIALER))
            yield put(buyUserActions.setLoadingExistingInformation(false))
        }
    } catch (e) {
        yield put(buyUserActions.setUserType(BUY_USER_TYPES.NEW))
        yield put(buyUserActions.setLoadingExistingInformation(false))
        yield call(authClass().logout)
    }
}

function* initializeLoggedInUcsSaga() {
    yield takeEvery(BuyUserActionTypes.GET_LOGGED_IN_USER_STATE_UCS, userLoggedInUcsSaga)
}

export default initializeLoggedInUcsSaga
